import React, { useEffect, useState } from "react";
import { SingleCardMetric } from "../ui/SingleCardMetric";

// Shared body for every shelter bed card.
//
// Each card reads one field out of the summary the API already computed
// (/reports/occupancy?filterLevel=org). Nothing here re-derives a count from raw bed rows:
// occupied + reserved + vacant = total is guaranteed server-side, and three cards each
// doing their own arithmetic over `occupant_id` is exactly how they used to disagree with
// each other - and how whole-dwelling and family-room occupancy went missing entirely.

// Named so that renaming a field on the API side is a compile error here rather than a card
// that quietly renders an em-dash - which is indistinguishable from the legitimate
// "no beds to divide by" case.
type ShelterBedMetricField =
  | "total_beds"
  | "occupied_beds"
  | "reserved_beds"
  | "vacant_beds"
  | "occupancy_rate"
  | "committed_rate"
  | "placement_efficiency";

interface ShelterBedMetricProps {
  data: any;
  title: string;
  subtitle?: string;
  tooltip?: string;
  field: ShelterBedMetricField;
  asPercent?: boolean;
}

export const ShelterBedMetric = ({
  data,
  title,
  subtitle = "",
  tooltip = "",
  field,
  asPercent = false,
}: ShelterBedMetricProps) => {
  // Absent data is not the same as pending data: the dashboard renders these tiles before
  // the fetch resolves, with a null occupancy when it fails, and with no data prop at all
  // while a card is being dragged. Settling after the first render means a failure shows an
  // em-dash instead of a spinner that never stops.
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    setIsLoading(false);
  }, [data]);

  const beds = data?.occupancy?.beds;
  const value = beds ? beds[field] : undefined;

  // the rates come back null when there are no beds to divide by
  const display =
    value === undefined || value === null ? "—" : asPercent ? `${(value * 100).toFixed(1)}%` : value.toString();

  return <SingleCardMetric title={title} subtitle={subtitle} tooltip={tooltip} total={display} isLoading={isLoading} />;
};
